import {Component, computed, inject, signal, viewChild, effect, ChangeDetectionStrategy} from "@angular/core"
import {MatTooltip} from "@angular/material/tooltip"
import {narrowInstance} from "@cm/utils"
import {MatMenuModule} from "@angular/material/menu"
import {MatDialog, MatDialogRef} from "@angular/material/dialog"
import {BaseInspectorComponent} from "@template-editor/components/inspectors/base-inspector/base-inspector.component"
import {ButtonComponent} from "@app/common/components/buttons/button/button.component"
import {SelectionPossibilities, ValueSlotComponent} from "@template-editor/components/value-slot/value-slot.component"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {ListItemComponent} from "@common/components/item/list-item/list-item.component"
import {RenameDialogComponent} from "@app/common/components/dialogs/rename-dialog/rename-dialog.component"
import {TemplateMenuComponent} from "@template-editor/components/template-menu/template-menu.component"
import {TemplateMenuSectionComponent} from "@template-editor/components/template-menu-section/template-menu-section.component"
import {InputContainerComponent} from "@app/common/components/inputs/input-container/input-container.component"
import {StringInputComponent} from "@app/common/components/inputs/string-input/string-input.component"
import {
    GeometryAttributeSet,
    PointGeometryAttributeSetPointLikeInput,
    CurveGeometryAttributeSetCurveLikeInput,
    MeshGeometryAttributeSetMeshLikeInput,
    PointGeometryAttributeSetRecordInput,
    CurveGeometryAttributeSetRecordInput,
    MeshGeometryAttributeSetRecordInput,
    GeometryAttributeSetInputLike,
    PointGeometryAttributeSet,
    CurveGeometryAttributeSet,
    TemplateGraph,
} from "@cm/template-nodes"
import {DIALOG_DEFAULT_WIDTH} from "@app/template-editor/helpers/constants"
import {createSelectorDummyNode} from "@template-editor/components/inspectors/geometry-graph-node-inspector/components/helpers"
import {tap} from "rxjs"
import {z} from "zod/v4-mini"

const inputOption = z.union([z.literal("record"), z.literal("object")])
type InputOption = z.infer<typeof inputOption>

class InputTypeSelectionDummyNode extends createSelectorDummyNode<{input: InputOption}>(z.object({input: inputOption}), "InputTypeSelectionDummyNode") {}

function isGeometryAttributeSetObjectLikeInput(input: GeometryAttributeSetInputLike) {
    return (
        input instanceof PointGeometryAttributeSetPointLikeInput ||
        input instanceof CurveGeometryAttributeSetCurveLikeInput ||
        input instanceof MeshGeometryAttributeSetMeshLikeInput
    )
}

function isGeometryAttributeSetRecordInput(input: GeometryAttributeSetInputLike) {
    return (
        input instanceof PointGeometryAttributeSetRecordInput ||
        input instanceof CurveGeometryAttributeSetRecordInput ||
        input instanceof MeshGeometryAttributeSetRecordInput
    )
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-geometry-attribute-set-inspector",
    templateUrl: "./geometry-attribute-set-inspector.component.html",
    styleUrls: ["./geometry-attribute-set-inspector.component.scss"],
    imports: [
        MatTooltip,
        MatMenuModule,
        ButtonComponent,
        ValueSlotComponent,
        InspectorSectionComponent,
        ListItemComponent,
        TemplateMenuComponent,
        TemplateMenuSectionComponent,
        InputContainerComponent,
        StringInputComponent,
    ],
    standalone: true,
})
export class GeometryAttributeSetInspectorComponent extends BaseInspectorComponent<GeometryAttributeSet> {
    readonly $inputTypeSelection = viewChild<ValueSlotComponent<InputTypeSelectionDummyNode>>("inputTypeSelection")
    private readonly dialog = inject(MatDialog)

    private readonly $triggerAttributeSetRecordInputKeysRecompute = signal(0)
    triggerAttributeSetRecordInputKeysRecompute() {
        this.$triggerAttributeSetRecordInputKeysRecompute.update((x) => x + 1)
    }

    readonly $attributeSetObjectLikeInput = computed(() => {
        const input = this.$parameters().input
        if (isGeometryAttributeSetObjectLikeInput(input)) return input
        return null
    })
    readonly $attributeSetPointLikeInput = computed(() => narrowInstance(this.$attributeSetObjectLikeInput(), PointGeometryAttributeSetPointLikeInput) ?? null)
    readonly $attributeSetCurveLikeInput = computed(() => narrowInstance(this.$attributeSetObjectLikeInput(), CurveGeometryAttributeSetCurveLikeInput) ?? null)
    readonly $attributeSetMeshLikeInput = computed(() => narrowInstance(this.$attributeSetObjectLikeInput(), MeshGeometryAttributeSetMeshLikeInput) ?? null)

    readonly $attributeSetRecordInput = computed(() => {
        const input = this.$parameters().input
        if (isGeometryAttributeSetRecordInput(input)) return input
        return null
    })
    readonly $attributeSetRecordInputKeys = computed(() => {
        const record = this.$attributeSetRecordInput()
        this.$triggerAttributeSetRecordInputKeysRecompute()
        if (!record) return []
        return Object.keys(record.parameters.record)
    })

    readonly $inputTypeSelectionDummyNode = signal(new InputTypeSelectionDummyNode({parameters: {input: "record"}}))

    constructor() {
        super()

        effect(() => {
            const input = this.$parameters().input
            this.$inputTypeSelectionDummyNode().updateParametersExplicit({
                input: isGeometryAttributeSetRecordInput(input) ? "record" : "object",
            })
            this.$inputTypeSelection()?.forceTriggerRecompute()
        })
    }

    onInputTypeSelectionChange(newInputType: unknown) {
        const parsedNewInputType = inputOption.safeParse(newInputType)
        if (!parsedNewInputType.success) return

        const node = this.$node()

        if (parsedNewInputType.data === "record") node.setRecordInput(null, this)
        else node.setObjectLikeInput(null, this)
    }

    readonly $addRecordFieldKey = signal<string>("")
    addRecordField() {
        const recordInput = this.$attributeSetRecordInput()
        if (!recordInput) return

        const key = this.$addRecordFieldKey()
        this.$addRecordFieldKey.set("")
        if (key === undefined || key.trim() === "") return
        recordInput.addRecordEntry(key, null, this)
        this.triggerAttributeSetRecordInputKeysRecompute()
    }

    deleteRecordField(key: string) {
        const recordInput = this.$attributeSetRecordInput()
        if (!recordInput) return
        recordInput.removeRecordEntry(key, this)
        this.triggerAttributeSetRecordInputKeysRecompute()
    }

    renameRecordField(oldKey: string) {
        const recordInput = this.$attributeSetRecordInput()
        if (!recordInput) return
        const currentRecordKeys = Object.keys(recordInput.parameters.record)
        if (!currentRecordKeys.includes(oldKey)) return

        const dialogRef: MatDialogRef<RenameDialogComponent> = this.dialog.open(RenameDialogComponent, {
            width: DIALOG_DEFAULT_WIDTH,
            data: {
                title: "Rename Attribute Set Field",
                currentName: oldKey,
            },
        })
        dialogRef
            .afterClosed()
            .pipe(
                tap((newKey) => {
                    if (newKey === oldKey) return
                    if (currentRecordKeys.includes(newKey)) throw new Error(`Attribute set field with name "${newKey}" already exists.`)
                    recordInput.renameRecordEntry(oldKey, newKey, this)
                    this.triggerAttributeSetRecordInputKeysRecompute()
                }),
            )
            .subscribe()
    }

    modifyTemplateGraph = (modifier: (x: TemplateGraph) => void) => this.sceneManagerService.modifyTemplateGraph(modifier)

    readonly $inputTypeSelectionPossibilities = computed<SelectionPossibilities<InputOption>>(() => {
        const node = this.$node()
        const objectLabel = node instanceof PointGeometryAttributeSet ? "point" : node instanceof CurveGeometryAttributeSet ? "curve" : "mesh"

        return [
            {name: "Record", value: "record"},
            {name: `From ${objectLabel}`, value: "object"},
        ]
    })
}
